import { fitText } from "@/lib/fit-title";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { absoluteUrl } from "@/config/sites";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { ACCOUNTING_QA_ALL, ACCOUNTING_QA_BY_SLUG } from "@/data/accounting/questions";
import { ACCOUNTING_REVIEWED_ISO, ACCOUNTING_REVIEWED_TH } from "@/data/accounting/facts";
import { JsonLd } from "@/components/JsonLd";

export const Route = createFileRoute("/accounting/q/$slug")({
  loader: ({ params }) => { if (!ACCOUNTING_QA_BY_SLUG[params.slug]) throw notFound(); return { slug: params.slug }; },
  head: ({ loaderData }) => {
    const qa = loaderData ? ACCOUNTING_QA_BY_SLUG[loaderData.slug] : undefined;
    if (!qa) return { meta: [{ title: ctrTitle("ไม่พบหน้า") }, { name: "robots", content: "noindex" }] };
    const url = absoluteUrl(`/accounting/q/${qa.slug}`);
    const title = fitText(`${qa.q} | Thai Notary Law`, 60);
    const desc = fitText(qa.a, 158);
    return { meta: [
      { title: ctrTitle(title) }, { name: "description", content: ctrDesc(desc) },
      { property: "og:title", content: ctrTitle(title) }, { property: "og:description", content: ctrDesc(desc) },
      { property: "og:url", content: url }, { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ], links: [{ rel: "canonical", href: url }] };
  },
  notFoundComponent: () => <div className="p-8 text-center">ไม่พบคำถามนี้</div>,
  errorComponent: () => <div className="p-8 text-center text-sm">โหลดหน้านี้ไม่สำเร็จ กรุณารีเฟรชอีกครั้ง</div>,
  component: AccountingQA,
});

function AccountingQA() {
  const { slug } = Route.useLoaderData();
  const qa = ACCOUNTING_QA_BY_SLUG[slug];
  if (!qa) throw notFound();
  const url = absoluteUrl(`/accounting/q/${qa.slug}`);
  const related = ACCOUNTING_QA_ALL.filter((x) => x.slug !== qa.slug && x.serviceSlug === qa.serviceSlug).slice(0, 8);
  const graph = { "@context": "https://schema.org", "@graph": [
    { "@type": "FAQPage", url, dateModified: ACCOUNTING_REVIEWED_ISO, mainEntity: [{ "@type": "Question", name: qa.q, acceptedAnswer: { "@type": "Answer", text: qa.a } }] },
    { "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "หน้าแรก", item: absoluteUrl("/") },
      { "@type": "ListItem", position: 2, name: "บัญชีและภาษี", item: absoluteUrl("/accounting") },
      { "@type": "ListItem", position: 3, name: "คำถาม", item: absoluteUrl("/accounting/questions") },
      { "@type": "ListItem", position: 4, name: qa.q, item: url },
    ] },
  ] };
  return <>
    <JsonLd data={graph} />
    <Breadcrumbs items={[{ label: "หน้าแรก", to: "/" }, { label: "บัญชีและภาษี", to: "/accounting" }, { label: "คำถาม", to: "/accounting/questions" }, { label: qa.q }]} />
    <section className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="mb-4 text-2xl font-bold">{qa.q}</h1>
      <p className="answer-summary faq-answer mb-6 leading-relaxed" data-speakable>{qa.a}</p>
      <p className="mb-6 text-xs text-muted-foreground">คำค้นที่เกี่ยวข้อง: {qa.synonyms.join(" · ")} · ข้อมูล ณ {ACCOUNTING_REVIEWED_TH}</p>
      {related.length > 0 && <><h2 className="mb-2 text-lg font-semibold">คำถามที่เกี่ยวข้อง</h2><ul className="mb-6 list-disc space-y-1 pl-5 text-sm">{related.map((r) => <li key={r.slug}><Link to="/accounting/q/$slug" params={{ slug: r.slug }} className="text-blue-700 hover:underline">{r.q}</Link></li>)}</ul></>}
      <Link to="/contact" className="text-sm text-blue-700 hover:underline">สอบถามทางโทรศัพท์ / LINE / อีเมล →</Link>
    </section>
  </>;
}